import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {RacesService} from '../shared/services/databaseServices/races/races.service';
import {EditService} from '../shared/services/editService/edit.service';
import {FormBuilder, Validators} from '@angular/forms';
import {SportTypesService} from '../shared/services/databaseServices/sportTypes/sport-types.service';
import {DatePipe} from '@angular/common';
import {faClone, faSave, faTrash} from '@fortawesome/free-solid-svg-icons';
import {SportType} from '../shared/classes/SportType';
import {Race} from '../shared/classes/Race';
import {Track} from '../shared/classes/Track';
import {Category} from '../shared/classes/Category';
import {MatDialog} from '@angular/material';
import {DialogComponent} from '../dialog/dialog.component';
import {FioConfig} from '../shared/classes/FioConfig';
import {CatTrack} from '../shared/classes/CatTrack';
import {CatTrackService} from '../shared/services/databaseServices/catTrackService/cat-track.service';
import {EmailTableService} from "../shared/services/databaseServices/emailTable/email-table.service";

@Component({
  selector: 'app-race-detail-info',
  templateUrl: './race-detail-info.component.html',
  styleUrls: ['./race-detail-info.component.css'],
  providers: [DatePipe]
})
export class RaceDetailInfoComponent implements OnInit, OnDestroy {

  private static NEW_ID = 'new';

  copyInterval = undefined;

  faSave = faSave;
  faTrash = faTrash;
  faClone = faClone;

  id = undefined;
  isNew = false;
  data = [];
  sportTypes: SportType[] = [];

  raceId;

  race: Race = undefined;
  catName = undefined;
  edit = false;
  trackName = undefined;
  editedTrack = false;

  displayedColumns: string[] = ['startTime', 'track', 'trackShort'];

  header = [
    {
      defName: 'startTime',
      translation: 'raceDetailPage.form.tab.startTime',
      elem: 'startTime'
    },
    {
      defName: 'track',
      translation: 'raceDetailPage.form.tab.track',
      elem: 'track'
    },
    {
      defName: 'trackShort',
      translation: 'raceDetailPage.form.tab.trackShort',
      elem: 'trackShort'
    }
  ];

  displayedColumnsCat: string[] = ['name', 'short', 'ageFrom', 'ageTo'];

  headerCat = [
    {
      defName: 'name',
      translation: 'settingPage.sportType.nameCat',
      elem: 'name'
    },
    {
      defName: 'short',
      translation: 'settingPage.sportType.nameCatShort',
      elem: 'short'
    },
    {
      defName: 'ageFrom',
      translation: 'settingPage.sportType.ageFrom',
      elem: 'ageFrom'
    },
    {
      defName: 'ageTo',
      translation: 'settingPage.sportType.ageTo',
      elem: 'ageTo'
    }
  ];

  public raceForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    dateOfStart: [ '', [Validators.required]],
    dateOfFinish: [ '', [Validators.required]],
    dateOfReg: ['', [Validators.required]],
    timeOfReg: ['23:59', [Validators.required, Validators.pattern('^[0-2][0-9]:[0-5][0-9]$')]],
    regOfMessage: ['', [Validators.required]],
    sportType: [ '', [Validators.required]],
    web: ['', []],
    location: ['', []],
    sameDate: [true, []],
    detail: ['', []],
    organizer: ['', []],
    personOr: ['', []],
    emailOr: ['', [Validators.email]],
    mobileOr: ['', []],
    rulesUrl: ['', []],
    gdprPlaceholder: ['', []]
  });

  public trackForm = this.formBuilder.group({
    startTime: ['', [Validators.required]],
    track: ['', [Validators.required]],
    trackShort: ['', [Validators.required]]
  });

  ageCatForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    short: ['', [Validators.required]],
    ageFrom: ['', [Validators.required]],
    ageTo: ['', [Validators.required]],
    customCat: [false, []]
  });

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private catTrackService: CatTrackService,
              private racesService: RacesService,
              private editService: EditService,
              private formBuilder: FormBuilder,
              private sportTypesService: SportTypesService,
              private emailTableService: EmailTableService,
              public dialog: MatDialog,
              private datePipe: DatePipe) { }

  ngOnInit() {
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    if(this.id === RaceDetailInfoComponent.NEW_ID && this.racesService.lastSave !== undefined) {
      this.id = this.racesService.lastSave;
    }
    this.isNew = this.id === RaceDetailInfoComponent.NEW_ID;
    this.loadSportTypes();
    this.changeRequired();
    if (this.isNew) {
      this.raceId = undefined;
      this.race = new Race();
      this.race.track = [];
      this.race.categories = [];
    } else {
      this.racesService.getOne(this.id).then(data => {
        this.raceId = data.id;
        this.createRace(data);
      });
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.copyInterval);
  }

  private createRace(object) {
    this.race = new Race();
    this.race.id = object.id;
    const item = object.data();
    this.race.name = item.name;
    this.race.dateStart = item.dateStart;
    this.race.dateFinish = item.dateFinish;
    this.race.dateOfReg = item.dateOfReg;
    this.race.regOfMessage = item.regOfMessage;
    this.race.track = item.track;
    this.race.defaultCat = item.defaultCat;
    this.race.categories = item.categories;
    this.race.web = item.web;
    this.race.location = item.location;
    this.race.sameDate = item.sameDate;
    this.race.sportType = item.sportType;
    this.race.organizer = item.organizer;
    this.race.emailOr = item.emailOr;
    this.race.mobileOr = item.mobileOr;
    this.race.detail = item.detail;
    this.race.startFee = item.startFee;
    this.race.fioConfig = item.fioConfig;
    this.race.rulesUrl = item.rulesUrl;
    this.race.gdprPlaceholder = item.gdprPlaceholder;
    this.race.sportType.get().then( type => {
      const sportType = new SportType();
      sportType.id = type.id;
      sportType.type = type.data().type;
      sportType.category = type.data().category;
      this.race.sportType = sportType;
      this.fillRaceForm();
      this.racesService.race = this.race;
    });
  }

  private fillRaceForm(){
    console.dir(this.race);
    this.raceForm.patchValue({
      name: this.race.name,
      dateOfStart: this.race.dateStart.toDate(),
      dateOfFinish: this.race.dateFinish.toDate(),
      dateOfReg: new Date(this.race.dateOfReg),
      timeOfReg: this.datePipe.transform(new Date(this.race.dateOfReg).getTime(), 'HH:mm'),
      regOfMessage: this.race.regOfMessage,
      sportType: this.race.sportType.id,
      web: this.race.web,
      location: this.race.location,
      sameDate: this.race.sameDate,
      organizer: this.race.organizer,
      emailOr: this.race.emailOr,
      mobileOr: this.race.mobileOr,
      detail: this.race.detail,
      rulesUrl: this.race.rulesUrl,
      gdprPlaceholder: this.race.gdprPlaceholder
    });

    this.ageCatForm.patchValue({
      customCat: this.race.defaultCat
    });
  }

  updateCatTrack() {
    const categories: Category[] = this.race.categories;
    const tracks: Track[] = this.race.track;
    const catTrack: CatTrack[] = [];

    tracks.forEach(track => {
      const catT = new CatTrack();
      catT.track = track;
      catT.cat = categories;
      catT.cat.forEach(c => {
        c.available = false;
        return c;
      });
      catTrack.push(catT);
    });
    this.catTrackService.update(this.raceId, catTrack).then(() => {

    });
  }

  cloneCatTrack(race:Race) {
    const categories: Category[] = race.categories;
    const tracks: Track[] = race.track;
    const catTrack: CatTrack[] = [];

    tracks.forEach(track => {
      const catT = new CatTrack();
      catT.track = track;
      catT.cat = categories;
      catT.cat.forEach(c => {
        c.available = false;
        return c;
      });
      catTrack.push(catT);
    });

    this.catTrackService.create(race.id, catTrack).then(() => {
      this.catTrackService.catTrack = catTrack;
    }).catch(err => console.dir(err));
  }

  createCatTrack() {
    const categories: Category[] = this.race.categories;
    const tracks: Track[] = this.race.track;
    const catTrack: CatTrack[] = [];

    tracks.forEach(track => {
      const catT = new CatTrack();
      catT.track = track;
      catT.cat = categories;
      catT.cat.forEach(c => {
        c.available = false;
        return c;
      });
      catTrack.push(catT);
    });
    this.catTrackService.create(this.raceId, catTrack).then(() => {
      this.catTrackService.catTrack = catTrack;
    }).catch(err => console.dir(err));
  }

  create() {
    console.log('save');
    if (this.raceId === undefined) {
      const now = Date.now().toString();
      this.raceId = now;
      this.racesService.lastSave = now;
    }
    this.race.name = this.raceForm.get('name').value;
    this.race.dateStart = this.raceForm.get('dateOfStart').value;
    this.race.dateFinish = this.raceForm.get('dateOfFinish').value;
    this.race.rulesUrl = this.raceForm.get('rulesUrl').value;

    let x = this.raceForm.get('dateOfStart').value;
    try {
      this.race.dateStart = this.race.dateStart.toDate();
      this.race.dateFinish = this.race.dateFinish.toDate();
    } catch (e) {
      console.warn("not a moment");
    }

    this.race.dateStart.seconds = this.race.dateStart.getTime() / 1000;
    this.race.dateFinish.seconds = this.race.dateFinish.getTime() / 1000;

    console.dir(this.race);

    const d = new Date(2020, 1, 1, 0, 0, 0);
    const t = this.raceForm.get('timeOfReg').value.toString().split(':');
    const now = new Date(2020, 1, 1, t[0], t[1], 0);
    const diff = now.getTime() - d.getTime();


    this.race.dateOfReg = new Date(this.raceForm.get('dateOfReg').value).getTime() + diff;
    console.dir(this.race.dateOfReg);
    this.race.regOfMessage = this.raceForm.get('regOfMessage').value;
    this.race.sportType = this.raceForm.get('sportType').value;
    this.race.defaultCat = this.ageCatForm.get('customCat').value;
    this.race.web = this.raceForm.get('web').value;
    this.race.location = this.raceForm.get('location').value;
    this.race.sameDate = this.raceForm.get('sameDate').value;
    this.race.organizer = this.raceForm.get('organizer').value;
    this.race.emailOr = this.raceForm.get('emailOr').value;
    this.race.mobileOr = this.raceForm.get('mobileOr').value;
    this.race.detail = this.raceForm.get('detail').value;
    this.race.startFee = this.race.startFee !== undefined ? this.race.startFee : [];
    this.race.fioConfig = this.race.fioConfig !== undefined ? this.race.fioConfig : {};
    this.race.gdprPlaceholder = this.raceForm.get("gdprPlaceholder").value;

    if(this.isNew) {
      this.racesService.createRace(this.raceId, this.race).then(() => {
        this.createCatTrack();
        if (this.race.startFee.length > 0) {
          this.router.navigate(['/admin/races']);
        } else {
          const dialogRef = this.dialog.open(DialogComponent, {
            width: '250px',
            data: {
              header: 'raceDetailPage.modal.startFeeHeader',
              text: 'raceDetailPage.modal.startFeeText',
              actionButton: 'raceDetailPage.modal.startFeeClose',
              warning: true
            }
          });

          dialogRef.afterClosed().subscribe(result => {
          });
        }
      }).catch(err => {
        console.dir(err);
      });
    } else {
      console.dir(this.race);
      this.racesService.update(this.raceId, this.race).then(() => {
        this.updateCatTrack();
        if (this.race.startFee.length > 0) {
          this.router.navigate(['/admin/races']);
        } else {
          const dialogRef = this.dialog.open(DialogComponent, {
            width: '250px',
            data: {
              header: 'raceDetailPage.modal.startFeeHeader',
              text: 'raceDetailPage.modal.startFeeText',
              actionButton: 'raceDetailPage.modal.startFeeClose',
              warning: true
            }
          });

          dialogRef.afterClosed().subscribe(result => {
          });
        }
      }).catch(err => {
        console.dir(err);
      });
    }
  }

  copyRace() {
    const cloneRace = this.race;
    cloneRace.name = cloneRace.name + " COPY";
    cloneRace.id = String(new Date().getTime());
    cloneRace.sportType = cloneRace.sportType.id;
    cloneRace.dateFinish = cloneRace.dateFinish.seconds * 1000;
    cloneRace.dateStart = cloneRace.dateStart.seconds * 1000;
    console.dir(this.race);
    this.emailTableService.getOne(this.raceId).then((data) => {
      const emails = data.data().emails;
      this.emailTableService.save(cloneRace.id, emails).then(() => {
      }).catch((err) => {
        console.log(err);
      });
    });

    this.racesService.createRace(cloneRace.id, cloneRace).then(() => {
      this.cloneCatTrack(cloneRace);
      console.dir(cloneRace.id);
      this.router.navigate(['/admin/races']);
    })

  }

  remove() {
    this.racesService.delete(this.raceId).then( () => {
      this.router.navigate(['/admin/races']);
    }).catch( err => {
      console.dir(err);
    });
  }

  loadSportTypes() {
    this.sportTypesService.getSportTypes().then( data => {
      const types = [];
      data.forEach( item => {
        const sport: SportType = new SportType();
        sport.id = item.id;
        sport.type = item.data().type;
        types.push(sport);
      });
      this.sportTypes = types;
    });
  }

  changeRequired() {
    if (this.raceForm.get('sameDate').value) {
      this.raceForm.get('dateOfFinish').disable();
      this.raceForm.get('dateOfFinish').clearValidators();
      this.copyInterval = setInterval( () => {
        this.raceForm.patchValue({dateOfFinish: this.raceForm.get('dateOfStart').value});
      }, 400);
    } else {
      clearInterval(this.copyInterval);
      this.raceForm.get('dateOfFinish').enable();
      this.raceForm.get('dateOfFinish').setValidators(Validators.required);
    }
  }

  changeCustomCat() {
    if (this.ageCatForm.get('customCat').value) {
      this.ageCatForm.get('name').disable();
      this.ageCatForm.get('ageTo').disable();
      this.ageCatForm.get('ageFrom').disable();
      this.loadDefaultCat();
    } else {
      this.ageCatForm.get('name').enable();
      this.ageCatForm.get('ageTo').enable();
      this.ageCatForm.get('ageFrom').enable();
    }
  }

  loadDefaultCat() {
    if(this.raceForm.get('sportType').value !== undefined) {
      this.sportTypesService.getOneSportType(this.raceForm.get('sportType').value).then(data => {
        this.race.categories = data.data().category;
      });
    } else {
      this.race.categories = [];
    }
  }

  isEdit() {
    return this.editService.editable;
  }

  createTrack() {
    let localData = this.race.track;
    const item: Track = {
      startTime: this.trackForm.get('startTime').value,
      track: this.trackForm.get('track').value,
      trackShort: this.trackForm.get('trackShort').value,
    };

    if (this.trackName !== undefined) {
      localData = localData.filter( t => t.track !== this.trackName );
    }

    localData.push(item);
    this.race.track = localData;
    this.trackForm.reset();
  }

  isExistTrack() {
    const name = this.trackForm.get('track').value;
    return this.race.track.map(item => item.track).indexOf(name) === -1 || this.editedTrack;
  }

  removeTrack() {
    this.race.track = this.race.track.filter( t => t.track !== this.trackName);
    this.trackForm.reset();
  }

  editTrack(item) {
    this.trackName = item.track;
    this.editedTrack = true;
    this.trackForm.patchValue({
      track: item.track,
      trackShort: item.trackShort,
      startTime: item.startTime
    });
  }

  getData() {
    if(this.race === undefined){
      return [];
    }
    return this.race.track;
  }

  getCategories() {
    if(this.race === undefined) {
      return [];
    }
    return this.race.categories;
  }

  isExist() {
    const name = this.ageCatForm.get('name').value;
    return this.race.categories.map(item => item.name).indexOf(name) === -1 || this.edit;
  }

  createCat() {
    const cat: Category = new Category();
    cat.name = this.ageCatForm.get('name').value;
    cat.short = this.ageCatForm.get('short').value;
    cat.ageFrom = this.ageCatForm.get('ageFrom').value;
    cat.ageTo = this.ageCatForm.get('ageTo').value;

    if (this.catName !== undefined) {
      this.race.categories = this.race.categories.filter( item => item.name !== this.catName );
    }

    this.race.categories.push(cat);
    this.ageCatForm.reset();
    this.edit = false;
  }

  removeCat() {
    this.race.categories = this.race.categories.filter( item => item.name !== this.catName);
    this.catName = undefined;
    this.ageCatForm.reset();
  }

  editCat(item) {
    this.catName = item.name;
    this.edit = true;
    this.ageCatForm.patchValue({
      name: item.name,
      short: item.short,
      ageFrom: item.ageFrom,
      ageTo: item.ageTo
    });
  }

}
